<template>
  <div class="avatar" :class="size">
    <img v-if="avatarUrl" :src="avatarUrl" />
    <img v-else src="~/assets/images/avatar.png" />
    <button
      v-if="editable && (isPlaceholder || !familyMember || isCurrentUser)"
      type="button"
      class="button-update-avatar"
      @click="handleClickUpdatePlaceHolderAvatar"
    >
      <icon name="camera" size="1.8rem"></icon>
    </button>
  </div>
</template>
<script setup lang="ts">
import { getServices } from '~/db/services';

interface Props {
  memberId?: string;
  size: 'normal' | 'small' | 'very-small';
  editable?: boolean;
  autoEdit?: boolean;
}

const props = defineProps({
  memberId: null,
  size: { default: 'normal' },
  editable: { type: Boolean, default: false },
  autoEdit: { type: Boolean, default: true }
});

const emit = defineEmits(["edited", "clickEdit"]);

const { $db } = useNuxtApp();
const { mediasService } = getServices($db);

const context = useContextStore();

const isCurrentUser = computed(() => props?.memberId === context.memberId);

const profile = useProfileStore();

const familyMember = computed(() => {
  if (!profile.familyMembers) return null;
  const m = profile.familyMembers.find((member) => {
    if (
      member.memberId === context.memberId ||
      member.memberId === props.memberId
    ) {
      return true;
    }
    return false;
  });
  return m;
});

const isPlaceholder = computed<boolean>(
  () => familyMember.value?.isPlaceholder ?? false
);

const mediaAvatar = ref<MediaSchema>();

const avatarId = computed(() => {
  if (isCurrentUser.value) {
    return profile.avatar;
  } else if (familyMember.value) {
    return familyMember.value.avatar;
  } else {
    return profile.family.familyAvatar;
  }
});
const {
  openWindow: openWindowUpdateAvatar,
  onUpdated: onProfileAvatarUpdated,
} = useProfileUpdateAvatar();
onProfileAvatarUpdated(() => emit('edited'));

const {
  openWindow: openWindowFamilyPlaceHolderAvatar,
  onUpdated: onFamilyPlaceHolderAvatarUpdated,
} = useFamilyPlaceHolderUpdateAvatar(props?.memberId);
onFamilyPlaceHolderAvatarUpdated(() => emit('edited'));

const { openWindow: openWindowFamilyAvatar, onUpdated: onFamilyAvatarUpdated } =
  useFamilyUpdateAvatar();
onFamilyAvatarUpdated(() => emit('edited'));

const avatarUrl = computed(() => {
  if (mediaAvatar.value) return mediaAvatar.value.urlCompressed;
  if (props.memberId) {
    return '/images/avatar.png';
  } else {
    return '/images/family/avatar.svg';
  }
});

const handleClickUpdatePlaceHolderAvatar = () => {
  if (props.autoEdit) {
    if (isPlaceholder.value) {
      openWindowFamilyPlaceHolderAvatar();
    } else if (isCurrentUser.value) {
      openWindowUpdateAvatar();
    } else {
      openWindowFamilyAvatar();
    }
  } else {
    emit('clickEdit');
  }
};

const loadAvatarUrl = async () => {
  if (!avatarId.value) return null;
  if (mediaAvatar.value && mediaAvatar.value?.id) {
    if (mediaAvatar.value.id === avatarId.value) return;
  }
  mediaAvatar.value = await mediasService.get({ id: avatarId.value });
};

watchEffect(() => {
  loadAvatarUrl();
});
</script>
<style lang="scss" scoped>
.avatar {
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;

  img {
    width: auto;
    max-height: 20rem;
    object-fit: contain;
    &.solid {
      border-radius: 50%;
      background-color: var(--color_neutral_50);
    }
  }
  &.small {
    img {
      height: 10rem;
    }
  }

  &.very-small {
    img {
      height: 6rem;
    }
  }
  .button-update-avatar {
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 3.4rem;
    height: 3.4rem;
    margin-left: 3rem;
    transition: all 300ms $ease-out-quad;
    border: 0.4rem solid var(--color_secondary);
    border-radius: 50%;
    background-color: var(--color_secondary);
    .icon {
      color: var(--color_neutral_50);
    }
    &:hover,
    &:focus {
      border: 0.2rem solid var(--color_secondary);
      background-color: var(--color_neutral_50);
      .icon {
        color: var(--color_secondary);
      }
    }
  }
}
</style>
